import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Divider, RadioButton } from '@sravni/react-design-system';
import { RadioButtonOption } from '@sravni/react-design-system/dist/types/components/RadioButton/types/RadioButton.types';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Dialog } from '@sravni/react-design-system/lib/Dialog';
import { Select, SelectValue } from '@sravni/react-design-system/lib/Select';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';

import { DEFAULT_UNKNOWN_TAG, FILTERS, FILTERS_NAME, PRODUCT_CATEGORY_LABELS } from '@constants/filters';
import { OrganizationType } from '@constants/organizationType';
import { sendFilterEvent } from '@helpers/analyticsEvents';
import { generateOptions } from '@helpers/generateOptions';
import { useOneFilter } from '@store/hooks/filters';
import { useActiveProductTags, useVisibleActiveProductCategory } from '@store/hooks/productTags';
import { setOneFilter } from '@store/reducers/filters';
import { setVisibleActiveProductCategory } from '@store/reducers/productTags';

import { useOrganizationType } from '../../../../hooks/organizationType';

import styles from './product-modal.module.scss';

interface IModalProps {
  isModalOpened: boolean;
  handleClose: () => void;
}

export const ProductModal = React.memo<IModalProps>(({ isModalOpened, handleClose }) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const organizationType = useOrganizationType();
  const isBankPage = organizationType === OrganizationType.BANK;

  const activeProductTags = useActiveProductTags();
  const [currentTag, setCurrentTag] = useState<FILTERS[FILTERS_NAME.REVIEW_TAG]>(
    useOneFilter(FILTERS_NAME.REVIEW_TAG) as FILTERS[FILTERS_NAME.REVIEW_TAG],
  );

  const productCategoryOptions =
    isBankPage && (generateOptions(PRODUCT_CATEGORY_LABELS) as unknown as RadioButtonOption[]);
  const visibleActiveProductCategory = useVisibleActiveProductCategory();

  const handleProductFilterChange = (productType: SelectValue) => {
    const tag = activeProductTags.options.find(({ value }) => value === productType) || DEFAULT_UNKNOWN_TAG;
    setCurrentTag(tag);
  };

  const handleButtonClick = () => {
    dispatch(setOneFilter({ filterName: FILTERS_NAME.REVIEW_TAG, value: currentTag }));
    dispatch(
      setOneFilter({ filterName: FILTERS_NAME.PRODUCT_CATEGORY_BY_PERSON, value: visibleActiveProductCategory }),
    );

    sendFilterEvent({
      filterName: FILTERS_NAME.REVIEW_TAG,
      filterValue: currentTag?.label,
    });
    sendFilterEvent({
      filterName: FILTERS_NAME.PRODUCT_CATEGORY_BY_PERSON,
      filterValue: PRODUCT_CATEGORY_LABELS[visibleActiveProductCategory],
    });
    handleClose();
  };

  const handleChangeCategory = (productCategory) => {
    dispatch(setVisibleActiveProductCategory(productCategory));
    setCurrentTag(DEFAULT_UNKNOWN_TAG);
  };

  return (
    <Dialog onClose={handleClose} visible={isModalOpened} fullscreen={isMobile}>
      <Dialog.Header title={'Вид продукта'} />
      <Dialog.Content className={styles.content}>
        {isBankPage && (
          <>
            <Text size={isMobile ? 14 : 16} strong>
              Тип продукта
            </Text>
            <RadioButton
              className={styles.radioButtons}
              options={productCategoryOptions}
              size={isMobile ? 36 : 44}
              value={visibleActiveProductCategory}
              onChange={handleChangeCategory}
            />
            {!isMobile && <Divider size={20} className={styles.divider} />}
          </>
        )}

        <Text className={styles.secondHeader} size={isMobile ? 14 : 16} strong>
          Выбор отдельного продукта
        </Text>
        <Select
          value={currentTag.value}
          options={activeProductTags?.options}
          onChange={handleProductFilterChange}
          className={styles.select}
        />
        <Button variant="primary" color="blue" block className={styles.button} onClick={handleButtonClick} size={52}>
          Показать рейтинг
        </Button>
      </Dialog.Content>
    </Dialog>
  );
});
